import { useEffect, useState } from "react";
import { X, Mail, Phone, MapPin, CalendarDays, Timer, Save, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import { GermanDatePicker } from "@/components/ui/date-picker";

export const fmtDE = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};
export const durationLabel = (months) => (months ? `${months} Monat${months === 3 ? "e" : ""}` : "nicht gesetzt");
export const statusStyle = (status) => ({
  active: "text-green-400 border-green-500/40 bg-green-500/10",
  not_started: "text-sky-300 border-sky-400/40 bg-sky-400/10",
  expired: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  completed: "text-green-400 border-green-500/50 bg-green-500/10",
  no_duration: "text-neutral-400 border-white/20 bg-white/5",
}[status] || "text-neutral-400 border-white/20");
export const restLabel = (s) => {
  if (s.pr_status === "completed") return "abgeschlossen";
  if (s.pr_status === "not_started") return s.days_to_start ? `Start in ${s.days_to_start} Tagen` : "Start heute";
  if (s.pr_status === "expired") return "abgelaufen";
  if (s.pr_status === "no_duration") return "—";
  return `Noch ${s.days_left} Tage`;
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-4 py-2 border-b border-white/5">
    <span className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
    <span className="text-sm text-neutral-100 text-right" data-testid={testId}>{value || "—"}</span>
  </div>
);

export const PlatzreifeDetail = ({ client, student, canEditDetails, onClose, onSaved }) => {
  const [birth, setBirth] = useState(student.birth_date || "");
  const [start, setStart] = useState(student.pr_start || "");
  const [months, setMonths] = useState(student.pr_duration_source === "manual" ? String(student.pr_duration_months) : "0");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.post("/admin/platzreife/details", {
        email: student.email, birth_date: birth || "", pr_start: start || "", pr_duration_months: Number(months) || 0,
      });
      toast.success("Daten gespeichert");
      onSaved?.(data.student);
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-6" data-testid="platzreife-detail-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <div className="relative bg-[#0A0A0A] border border-[#ff0033]/40 w-full max-w-3xl max-h-[92vh] overflow-auto">
        <div className="flex items-start justify-between gap-4 p-4 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <p className="font-mono-lab text-[10px] tracking-widest text-[#ff0033] uppercase">// Platzreife-Akte</p>
            <h3 className="font-heading text-xl uppercase text-white mt-1" data-testid="platzreife-detail-name">{student.first_name} {student.last_name}</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className={`font-mono-lab text-[10px] uppercase tracking-widest px-2 py-1 border ${statusStyle(student.pr_status)}`} data-testid="platzreife-detail-status">{student.pr_status_label}</span>
            <button onClick={onClose} data-testid="platzreife-detail-close" className="text-neutral-400 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          <section>
            <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Persönliche Daten</p>
            <Row label="Vorname" value={student.first_name} />
            <Row label="Nachname" value={student.last_name} />
            <Row label="Geburtsdatum" testId="platzreife-detail-birth" value={student.birth_date ? fmtDE(student.birth_date) : "Nicht hinterlegt"} />
            <Row label="Alter" testId="platzreife-detail-age" value={student.age !== null && student.age !== undefined ? `${student.age} Jahre` : "—"} />
            <Row label="E-Mail" value={<a href={`mailto:${student.email}`} className="hover:text-[#ff0033] break-all inline-flex items-center gap-1.5"><Mail size={13} /> {student.email}</a>} />
            <Row label="Telefon" value={student.phone ? <a href={`tel:${student.phone}`} className="hover:text-[#ff0033] inline-flex items-center gap-1.5"><Phone size={13} /> {student.phone}</a> : "—"} />
            <Row label="Adresse" value={student.address ? <span className="inline-flex items-start gap-1.5"><MapPin size={13} className="mt-0.5" /> {student.address}</span> : "keine Adresse hinterlegt"} />
          </section>

          <section>
            <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Platzreife</p>
            <Row label="Modell" testId="platzreife-detail-model" value={student.pr_model || student.course_name} />
            <Row label="PR-Start" testId="platzreife-detail-start" value={`${fmtDE(student.pr_start) || "—"}${student.pr_start_source === "manual" ? " (manuell)" : ""}`} />
            <Row label="PR-Laufzeit" testId="platzreife-detail-duration" value={`${durationLabel(student.pr_duration_months)}${student.pr_duration_source === "manual" ? " (manuell)" : student.pr_duration_months ? " (automatisch)" : ""}`} />
            <Row label="PR-Ende" testId="platzreife-detail-end" value={fmtDE(student.pr_end) || "—"} />
            <Row label="Restlaufzeit" testId="platzreife-detail-rest" value={restLabel(student)} />
            <Row label="Kurszeitraum" value={`${fmtDE(student.period_start) || "—"}${student.period_end ? ` – ${fmtDE(student.period_end)}` : ""}`} />
            <Row label="Fortschritt" testId="platzreife-detail-progress" value={`${student.done} / ${student.total} – ${student.percent} %`} />
            <div className="h-1.5 bg-white/10 mt-2 w-full">
              <div className={`h-full ${student.completed ? "bg-green-500" : "bg-[#ff0033]"}`} style={{ width: `${student.percent}%` }} />
            </div>
            {student.completed && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-green-400 font-mono-lab text-[10px] uppercase tracking-widest">
                <Award size={12} /> Abgeschlossen{student.completed_at ? ` · ${fmtDE(student.completed_at)}` : ""}
              </p>
            )}
          </section>
        </div>

        {canEditDetails && (
          <div className="p-4 border-t border-white/10" data-testid="platzreife-detail-edit">
            <p className="font-mono-lab text-[10px] uppercase tracking-widest text-[#ff0033] mb-3">Nachtragen / korrigieren (nur Admin)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase flex items-center gap-1.5"><CalendarDays size={12} /> Geburtsdatum</span>
                <GermanDatePicker value={birth} onChange={setBirth} placeholder="TT.MM.JJJJ" className="p-2.5" testId="platzreife-edit-birth" />
              </label>
              <label className="block">
                <span className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase flex items-center gap-1.5"><CalendarDays size={12} /> PR-Start</span>
                <GermanDatePicker value={start} onChange={setStart} placeholder="Automatisch (Kursstart)" className="p-2.5" testId="platzreife-edit-start" />
              </label>
              <label className="block">
                <span className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase flex items-center gap-1.5"><Timer size={12} /> PR-Laufzeit</span>
                <select value={months} onChange={(e) => setMonths(e.target.value)} data-testid="platzreife-edit-duration"
                  className="w-full bg-[#141414] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033]">
                  <option value="0">Automatisch aus Kurs</option>
                  <option value="1">1 Monat</option>
                  <option value="3">3 Monate</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={save} disabled={saving} data-testid="platzreife-detail-save"
                className="flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-2.5 hover:bg-[#cc0029] disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatzreifeDetail;
